import { Suspense, useEffect, useState, useMemo, useRef, lazy } from 'react';
import { getUserUsername, getUserEntityData } from '@/redux/user/selectors';
import type { RequestErrorInfo, EntityData } from '@/services/api/types';
import { RequestError, FADE_DURATION } from '@CS/components/Popups';
import type { Username as UserName } from '@/redux/user/types';
import { Username, Leaderboard } from '@/components/Popups';
import { DELTA_UPDATE } from '@CS/utils/Number';
import type { AppProps } from '@JR/app/types';
import { capitalize } from '@CS/utils/String';
import { useParams } from 'react-router-dom';
import type { ComponentType } from 'react';
import Loader from '@CS/components/Loader';
import { connect } from 'react-redux';
import API from '@/services/api';

type GameProps = {
  toggleLandscape?: (landscape?: boolean) => void;
  username: UserName;
  data?: EntityData;
};

const Game = ({ toggleLandscape, username, data }: GameProps) => {
  const [requestError, setRequestError] = useState<RequestErrorInfo>();
  const [visibleLeaderboard, setVisibleLeaderboard] = useState(false);
  const [closedLeaderboard, setClosedLeaderboard] = useState(false);
  const [highScore, setHighScore] = useState<number | null>(null);
  const [visibleUsername, setVisibleUsername] = useState(false);

  const closeLeaderboard = () => setVisibleLeaderboard(false);
  const onCloseUsername = () => setVisibleUsername(false);
  const cancelRequest = () => setRequestError(undefined);

  const [onboarding, setOnboarding] = useState(false);
  const [restart, setRestart] = useState(false);
  const highestScore = useRef<number>(null);
  const index = +(useParams().index ?? 0);
  const participant = useRef('');

  const showLeaderboard = () =>
    ~setClosedLeaderboard(false) && setVisibleLeaderboard(true);

  const hideLeaderboard = () =>
    ~setClosedLeaderboard(true) && setTimeout(closeLeaderboard, FADE_DURATION);

  const onGameStart = () =>
    API.createParticipant(index)
      .then(({ id }) => (participant.current = id))
      .catch((error) => setRequestError(API.getRequestErrorInfo(error)));

  const onGameOver = (score: number) => {
    username === null && setVisibleUsername(true);

    setHighScore(
      (highestScore.current = Math.max(score, highestScore.current ?? 0))
    );

    API.submitScore(participant.current, { score }).catch((error) =>
      setRequestError(API.getRequestErrorInfo(error))
    );

    setRestart(false);
  };

  const onOnboarding = () => {
    const onboarding = (data?.onboarding as boolean[]) ?? [false, false, false];

    if (onboarding[index]) return;
    else onboarding[index] = true;

    API.updateUser({ data: { ...data, onboarding } }).catch((error) =>
      setRequestError(API.getRequestErrorInfo(error))
    );
  };

  const onRestart = (participant = true) => {
    setTimeout(() => setRestart(false), DELTA_UPDATE);
    participant && onGameStart();
    setRestart(true);
  };

  useEffect(() => {
    API.getHighestScore(index)
      .then((score) => setHighScore((highestScore.current = score)))
      .catch((error) => setRequestError(API.getRequestErrorInfo(error)));
  }, [index]);

  useEffect(() => {
    data && setOnboarding(!(data.onboarding as boolean[])?.[index]);
  }, [data]);

  const Game = useMemo(
    () =>
      lazy(
        ((index: number) =>
          Object.entries(
            import.meta.glob([
              '/DoodleJump/src/app/index.ts',
              '/JumpAndRun/src/app/index.ts',
              '/TapAndSmash/src/app/index.ts'
            ])
          ).find(([game]) => {
            return game.includes(
              API.getGameName(index)
                .replace(/\d$/, '')
                .split('-')
                .map(capitalize)
                .join('')
            );
          })?.[1] as () => Promise<{ default: ComponentType<AppProps> }>)(index)
      ),
    [index]
  );

  return (
    <Suspense fallback={<Loader image />}>
      <Game
        closeLeaderboard={hideLeaderboard}
        showLeaderboard={showLeaderboard}
        toggleLandscape={toggleLandscape}
        onOnboarding={onOnboarding}
        onGameStart={onGameStart}
        onGameOver={onGameOver}
        onboarding={onboarding}
        highScore={highScore}
        onRestart={onRestart}
        username={!!username}
        restart={restart}
      />

      {visibleLeaderboard && (
        <Leaderboard
          onClose={closeLeaderboard}
          close={closedLeaderboard}
          game={index}
        />
      )}

      {visibleUsername && (
        <Username
          onClose={onCloseUsername}
          landscape={index === 1}
          bold={index === 2}
        />
      )}

      {requestError && (
        <RequestError info={requestError} onClose={cancelRequest} />
      )}
    </Suspense>
  );
};

const mapStateToProps = (state: never) => ({
  username: getUserUsername(state),
  data: getUserEntityData(state)
});

export default connect(mapStateToProps)(Game);
